import React, { useRef, useEffect, useState } from 'react';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

export interface SegmentOption<T extends string> {
  id: T;
  label: string;
  count?: number;
  icon?: React.ReactNode;
  variant?: 'default' | 'emerald' | 'amber' | 'blue' | 'zinc';
}

interface IosSegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

export function IosSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className = '',
}: IosSegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number; ready: boolean }>({
    left: 0,
    width: 0,
    ready: false,
  });

  const updateIndicator = () => {
    if (!containerRef.current) return;
    const activeEl = containerRef.current.querySelector<HTMLButtonElement>(`[data-segment-id="${value}"]`);
    if (activeEl) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      setIndicatorStyle({
        left: activeRect.left - containerRect.left,
        width: activeRect.width,
        ready: true,
      });
    }
  };

  useEffect(() => {
    updateIndicator();
  }, [value, options]);

  // Recalculate on window resize
  useEffect(() => {
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [value]);

  const handleSelect = (id: T) => {
    if (id !== value) {
      haptics.selection();
      sounds.playTapSound();
      onChange(id);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center p-1 rounded-2xl bg-black/[0.05] dark:bg-zinc-950/70 border border-black/[0.04] dark:border-white/[0.08] backdrop-blur-2xl shadow-inner select-none transition-colors ${className}`}
      role="tablist"
    >
      {/* Sliding Spring Thumb Indicator */}
      {indicatorStyle.ready && (
        <div
          className="absolute top-1 bottom-1 rounded-xl bg-white border border-black/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_0_0_rgba(255,255,255,1)] dark:bg-zinc-800/90 dark:border-white/15 dark:shadow-[0_2px_12px_rgba(0,0,0,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)] pointer-events-none"
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
            transition: 'left 280ms cubic-bezier(0.32, 0.72, 0, 1), width 280ms cubic-bezier(0.32, 0.72, 0, 1)',
            willChange: 'left, width',
          }}
        />
      )}

      {/* Segment Option Buttons */}
      {options.map((opt) => {
        const isSelected = opt.id === value;
        return (
          <button
            key={opt.id}
            data-segment-id={opt.id}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => handleSelect(opt.id)}
            className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-semibold rounded-xl ios-press transition-colors ${
              isSelected
                ? 'text-zinc-900 dark:text-white'
                : 'text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-zinc-200'
            }`}
          >
            {opt.icon && <span className="shrink-0">{opt.icon}</span>}
            <span className="truncate">{opt.label}</span>

            {typeof opt.count === 'number' && (
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold transition-colors ${
                  isSelected
                    ? opt.variant === 'emerald'
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30'
                      : opt.variant === 'amber'
                      ? 'bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30'
                      : opt.variant === 'blue'
                      ? 'bg-blue-500/20 text-blue-800 dark:text-blue-300 border border-blue-500/30'
                      : 'bg-black/5 dark:bg-zinc-700/60 text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10'
                    : 'bg-black/[0.04] text-zinc-500 dark:bg-zinc-900 dark:text-zinc-500 border border-transparent dark:border-zinc-800'
                }`}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
